import React, { useState } from "react";
import { usePersistedState } from "@/hooks/usePersistedState";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./dialog";
import { Button } from "./button";
import { Input } from "./input";
import { Label } from "./label";
import {
  Search,
  MapPin,
  Phone,
  Clock,
  Loader2,
  AlertCircle,
  Building2,
  Globe,
  AlertTriangle,
  Info,
  Monitor,
  X,
} from "lucide-react";
import {
  searchPharmaciesByPostcode,
  isValidPostcode,
  isDSPService,
  type PharmacySearchResult,
} from "../nhs-templates/pharmacyService";
import { Alert, AlertDescription } from "./alert";
import useTitanApp from "../../hooks/useTitanApp";

interface PharmacySearchModalProps {
  trigger: React.ReactNode;
  onSelect: (pharmacy: PharmacySearchResult) => void;
  isDarkMode?: boolean;
  defaultPostcode?: string;
  showServiceTypeFilter?: boolean;
}

type PharmacyServiceType =
  | "urgent-medicine-supply"
  | "blood-pressure-check"
  | "contraception";

const SERVICE_TYPE_LABELS: Record<PharmacyServiceType, string> = {
  "urgent-medicine-supply": "Urgent Medicine Supply",
  "blood-pressure-check": "Hypertension",
  contraception: "Emergency Oral Contraception",
};

const SERVICE_TYPE_IDS: Record<PharmacyServiceType, string> = {
  "urgent-medicine-supply": "131",
  "blood-pressure-check": "148",
  contraception: "149",
};
function CapacityBadge({
  capacity,
  isDarkMode,
}: {
  capacity: PharmacySearchResult["capacity"];
  isDarkMode: boolean;
}) {
  const styles: Record<string, string> = {
    GREEN: isDarkMode
      ? "bg-green-900/40 text-green-300 border-green-700"
      : "bg-green-50 text-green-700 border-green-200",
    AMBER: isDarkMode
      ? "bg-amber-900/40 text-amber-300 border-amber-700"
      : "bg-amber-50 text-amber-700 border-amber-200",
    RED: isDarkMode
      ? "bg-red-900/40 text-red-300 border-red-700"
      : "bg-red-50 text-red-700 border-red-200",
  };

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${styles[capacity.rag] || styles.GREEN}`}
    >
      <span
        className="w-2 h-2 rounded-full"
        style={{ backgroundColor: capacity.hex }}
      />
      {capacity.human === "NONE" ? "At Capacity" : `${capacity.human} Capacity`}
    </span>
  );
}

export function PharmacySearchModal({
  trigger,
  onSelect,
  isDarkMode = false,
  defaultPostcode = "",
  showServiceTypeFilter = false,
}: PharmacySearchModalProps) {
  const [open, setOpen] = usePersistedState("pharmacySearchModal", false);
  const [postcode, setPostcode] = useState(defaultPostcode);
  const [searchResults, setSearchResults] = useState<PharmacySearchResult[]>(
    [],
  );
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPharmacy, setSelectedPharmacy] =
    useState<PharmacySearchResult | null>(null);
  const [selectedServiceType, setSelectedServiceType] = useState<
    PharmacyServiceType | ""
  >("");

  const { patientState } = useTitanApp();
  const { selectedPatientDetails } = patientState || {};
  const handleSearch = async () => {
    const trimmedPostcode = postcode.trim();

    if (trimmedPostcode?.length > 0 && !isValidPostcode(trimmedPostcode)) {
      setError("Please enter a valid UK postcode (e.g., BS1 3EA)");
      return;
    }
    if (showServiceTypeFilter && !selectedServiceType) {
      setError("Please select a service type");
      return;
    }
    // setSearchedPostcode(trimmedPostcode);
    if (!trimmedPostcode) {
      setError("Please enter a postcode");
      return;
    }

    if (!isValidPostcode(trimmedPostcode)) {
      setError("Please enter a valid UK postcode (e.g., BS1 3EA)");
      return;
    }

    setIsSearching(true);
    setError(null);
    setHasSearched(false);

    const gender =
      showServiceTypeFilter === true
        ? "0"
        : selectedPatientDetails?.sex === 1
          ? "M"
          : selectedPatientDetails?.sex === 2
            ? "F"
            : selectedPatientDetails?.sex === 3 ||
                selectedPatientDetails?.sex === 9
              ? "I"
              : "D";

    try {
      const serviceTypeId =
        showServiceTypeFilter && selectedServiceType
          ? SERVICE_TYPE_IDS[selectedServiceType]
          : undefined;
      const results = await searchPharmaciesByPostcode(
        trimmedPostcode,
        10,
        gender,
        setError,
        serviceTypeId,
      );
      setSearchResults(results);
      setHasSearched(true);

      // if (results.length === 0) {
      //   setError("No pharmacies found near this postcode");
      // }
    } catch (err) {
      console.error("Error searching pharmacies:", err);
      setError("Failed to search pharmacies. Please try again.");
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectPharmacy = () => {
    if (selectedPharmacy) {
      onSelect(selectedPharmacy);
      setOpen(false);
      handleReset();
    }
  };

  const handleClearResults = () => {
    setPostcode("");
    setSearchResults([]);
    setHasSearched(false);
    setError(null);
    setSelectedPharmacy(null);
  };

  const handleReset = () => {
    setPostcode(defaultPostcode);
    setSearchResults([]);
    setHasSearched(false);
    setError(null);
    setSelectedPharmacy(null);
    setSelectedServiceType("");
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      handleReset();
    }
  };

  const weekDays = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ] as const;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        className={`sm:max-w-[750px] max-h-[90vh] overflow-hidden flex flex-col transition-colors duration-300 ${
          isDarkMode
            ? "bg-gray-900 border-gray-700"
            : "bg-white border-gray-200"
        }`}
      >
        <DialogHeader>
          <DialogTitle
            className={`text-lg font-semibold transition-colors duration-300 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Search for Nearby Pharmacies
          </DialogTitle>
        </DialogHeader>

        {showServiceTypeFilter && (
          <div className="space-y-2">
            <Label
              className={`text-sm font-medium ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}
            >
              Service Type
            </Label>
            <div className="flex gap-2">
              {(
                Object.entries(SERVICE_TYPE_LABELS) as [
                  PharmacyServiceType,
                  string,
                ][]
              ).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setSelectedServiceType(value)}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border-2 transition-all ${
                    selectedServiceType === value
                      ? isDarkMode
                        ? "border-blue-500 bg-blue-950/30 text-blue-300"
                        : "border-blue-500 bg-blue-50 text-blue-700"
                      : isDarkMode
                        ? "border-gray-600 bg-gray-800 text-gray-300 hover:border-gray-500"
                        : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="space-y-4 flex-1 overflow-y-auto">
          {/* Search Section */}
          <div className="space-y-3">
            <Label
              htmlFor="postcode"
              className={`text-sm font-medium transition-colors duration-300 ${
                isDarkMode ? "text-gray-200" : "text-gray-700"
              }`}
            >
              Enter Postcode
            </Label>
            <div className="flex gap-2">
              <div className="flex-1">
                <Input
                  id="postcode"
                  value={postcode}
                  onChange={(e) => setPostcode(e.target.value.toUpperCase())}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSearch();
                    }
                  }}
                  placeholder="e.g., BS1 3EA"
                  className={`focus:ring-inset focus:ring-1 ${isDarkMode ? "bg-gray-800 border-gray-600 text-gray-200 focus:ring-blue-500" : "focus:ring-blue-500"}`}
                />
              </div>
              <Button
                onClick={handleSearch}
                disabled={isSearching}
                className={
                  isDarkMode
                    ? "bg-white text-black hover:bg-gray-200"
                    : "bg-slate-900 hover:bg-slate-800"
                }
              >
                {isSearching ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    Search
                  </>
                )}
              </Button>
              {hasSearched && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClearResults}
                  className={`flex-shrink-0 ${
                    isDarkMode
                      ? "border-gray-600 text-gray-300 hover:bg-gray-800 hover:text-gray-100"
                      : "border-gray-300 text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  <X className="w-4 h-4 mr-1.5" />
                  Clear
                </Button>
              )}
            </div>

            {error && (
              <Alert
                className={
                  isDarkMode
                    ? "bg-red-950/20 border-red-800"
                    : "bg-red-50 border-red-200"
                }
              >
                <AlertCircle
                  className={`h-4 w-4 ${isDarkMode ? "text-red-400" : "text-red-600"}`}
                />
                <AlertDescription
                  className={`text-xs ${isDarkMode ? "text-red-200" : "text-red-900"}`}
                >
                  {error}
                </AlertDescription>
              </Alert>
            )}
          </div>

          {/* Results Section */}
          {hasSearched && searchResults?.length > 0 && (
            <div className="space-y-3">
              <h4
                className={`text-sm font-semibold ${
                  isDarkMode ? "text-gray-200" : "text-gray-900"
                }`}
              >
                Found {searchResults?.length} pharmacies near {postcode}
                {/* Found {searchResults.length} pharmacies near {postcode} */}
              </h4>

              <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar ">
                {searchResults?.map((pharmacy) => (
                  <button
                    key={pharmacy.id}
                    onClick={() => setSelectedPharmacy(pharmacy)}
                    className={`w-full p-4 rounded-lg border-2 border-solid text-left transition-all ${
                      selectedPharmacy?.id === pharmacy.id
                        ? isDarkMode
                          ? "border-blue-500 bg-blue-950/30"
                          : "border-blue-500 bg-blue-50"
                        : isDSPService(pharmacy.odsCode)
                          ? isDarkMode
                            ? "border-purple-700 bg-purple-950/20 hover:border-purple-600"
                            : "border-purple-300 bg-purple-50/50 hover:border-purple-400"
                          : isDarkMode
                            ? "border-gray-700 bg-gray-800 hover:border-gray-600"
                            : "border-gray-200 bg-white hover:border-gray-300"
                    }`}
                  >
                    <div className="space-y-2">
                      {/* DSP / Online Service Banner */}
                      {isDSPService(pharmacy.odsCode) && (
                        <div
                          className={`flex items-center gap-2 px-2.5 py-1.5 rounded text-xs font-semibold ${
                            isDarkMode
                              ? "bg-purple-900/40 text-purple-300 border border-purple-700"
                              : "bg-purple-100 text-purple-800 border border-purple-200"
                          }`}
                        >
                          <Monitor className="w-3.5 h-3.5 flex-shrink-0" />
                          Online / Distance Selling Pharmacy (DSP)
                        </div>
                      )}

                      {/* Row 1: Name, Capacity, Distance */}
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <Building2
                            className={`w-5 h-5 flex-shrink-0 ${isDSPService(pharmacy.odsCode) ? (isDarkMode ? "text-purple-400" : "text-purple-600") : isDarkMode ? "text-blue-400" : "text-blue-600"}`}
                          />
                          <h5
                            className={`font-semibold text-sm truncate ${
                              isDarkMode ? "text-gray-100" : "text-gray-900"
                            }`}
                          >
                            {pharmacy.name}
                          </h5>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <CapacityBadge
                            capacity={pharmacy.capacity}
                            isDarkMode={isDarkMode}
                          />
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              pharmacy.distance < 1
                                ? isDarkMode
                                  ? "bg-green-900/40 text-green-300"
                                  : "bg-green-100 text-green-700"
                                : isDarkMode
                                  ? "bg-blue-900/40 text-blue-300"
                                  : "bg-blue-100 text-blue-700"
                            }`}
                          >
                            <MapPin className="w-3 h-3 mr-1" />
                            {pharmacy.distance} mi
                          </span>
                        </div>
                      </div>

                      {/* Capacity warning for RED status */}
                      {pharmacy.capacity.rag === "RED" && (
                        <div
                          className={`flex items-center gap-1.5 px-2 py-1 rounded text-[10px] ${
                            isDarkMode
                              ? "bg-red-950/30 text-red-300"
                              : "bg-red-50 text-red-700"
                          }`}
                        >
                          <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                          This service has reported being at capacity or unable
                          to accept new patients.
                        </div>
                      )}

                      {/* Service type & ODS code */}
                      <div
                        className={`text-[10px] ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                      >
                        {pharmacy.serviceType} &middot; ODS: {pharmacy.odsCode}
                      </div>

                      {/* Address */}
                      <div className="flex items-start gap-2">
                        <MapPin
                          className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        />
                        <div
                          className={`text-xs ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                        >
                          <div>{pharmacy.address}</div>
                          <div className="font-medium">{pharmacy.postcode}</div>
                        </div>
                      </div>

                      {/* Phones */}
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                        {pharmacy.telephone && (
                          <div className="flex items-center gap-2">
                            <Phone
                              className={`w-4 h-4 flex-shrink-0 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                            />
                            <span
                              className={`text-xs ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                            >
                              {pharmacy.telephone}
                            </span>
                          </div>
                        )}
                        {pharmacy.telephoneNonPublic && (
                          <div className="flex items-center gap-2">
                            <Phone
                              className={`w-4 h-4 flex-shrink-0 ${isDarkMode ? "text-amber-400" : "text-amber-600"}`}
                            />
                            <span
                              className={`text-xs ${isDarkMode ? "text-amber-300" : "text-amber-700"}`}
                            >
                              {pharmacy.telephoneNonPublic}
                              <span className="text-[10px] ml-1 opacity-70">
                                (Non-public)
                              </span>
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Website */}
                      {pharmacy.web && (
                        <div className="flex items-center gap-2">
                          <Globe
                            className={`w-4 h-4 flex-shrink-0 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          />
                          <span
                            className={`text-xs ${isDarkMode ? "text-blue-400" : "text-blue-600"}`}
                          >
                            {pharmacy.web}
                          </span>
                        </div>
                      )}

                      {/* Opening Hours - 7 Day Calendar Grid */}
                      <div className="mt-1">
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <Clock
                            className={`w-4 h-4 flex-shrink-0 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          />
                          <span
                            className={`text-xs font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                          >
                            {pharmacy.allHours
                              ? "Open All Hours (24/7)"
                              : "Opening Hours"}
                          </span>
                          {pharmacy.allHours && (
                            <span
                              className={`text-[10px] px-1.5 py-0.5 rounded ${
                                isDarkMode
                                  ? "bg-green-900/40 text-green-300"
                                  : "bg-green-100 text-green-700"
                              }`}
                            >
                              24/7
                            </span>
                          )}
                        </div>

                        {/* Standard weekly hours grid - show even for allHours services for context */}
                        {!pharmacy.allHours && (
                          <div className="grid grid-cols-7 gap-1">
                            {weekDays.map((day) => {
                              const hours = pharmacy.openingHours[day];
                              const hoursArray = Array.isArray(hours)
                                ? hours
                                : [hours];
                              const isClosed =
                                hoursArray.length === 1 &&
                                hoursArray[0] === "Closed";
                              return (
                                <div
                                  key={day}
                                  className={`rounded px-1 py-1.5 text-center text-[10px] leading-tight ${
                                    isClosed
                                      ? isDarkMode
                                        ? "bg-red-950/30 text-red-400"
                                        : "bg-red-50 text-red-500"
                                      : isDarkMode
                                        ? "bg-gray-700/50 text-gray-300"
                                        : "bg-gray-100 text-gray-700"
                                  }`}
                                >
                                  <div className="font-semibold capitalize mb-0.5">
                                    {day.slice(0, 3)}
                                  </div>
                                  {isClosed ? (
                                    <div>Closed</div>
                                  ) : (
                                    hoursArray.map((h, i) => (
                                      <div key={i}>{h}</div>
                                    ))
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Bank holiday hours */}
                        {pharmacy.openingHours.bankholiday && (
                          <div className="mt-1.5">
                            <div
                              className={`text-[10px] font-medium mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                            >
                              Bank Holidays
                            </div>
                            <div className="flex flex-wrap gap-1">
                              <div
                                className={`rounded px-1.5 py-1.5 text-center text-[10px] leading-tight min-w-[70px] ${
                                  pharmacy.openingHours.bankholiday[0] ===
                                  "Closed"
                                    ? isDarkMode
                                      ? "bg-red-950/30 text-red-400"
                                      : "bg-red-50 text-red-500"
                                    : isDarkMode
                                      ? "bg-amber-900/30 text-amber-300"
                                      : "bg-amber-50 text-amber-700"
                                }`}
                              >
                                {pharmacy.openingHours.bankholiday[0] ===
                                "Closed" ? (
                                  <div>Closed</div>
                                ) : (
                                  pharmacy.openingHours.bankholiday.map(
                                    (h, i) => <div key={i}>{h}</div>,
                                  )
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Specified Dates (override all other hours for that date) */}
                        {pharmacy.specificDates &&
                          pharmacy.specificDates.length > 0 && (
                            <div className="mt-2">
                              <div
                                className={`text-xs font-medium mb-1.5 flex items-center gap-1 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                              >
                                Specified Dates
                                <span
                                  className={`text-[9px] font-normal ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                                >
                                  (override standard hours)
                                </span>
                              </div>
                              <div className="flex flex-wrap gap-1">
                                {pharmacy.specificDates.map((entry) => {
                                  const isClosed = entry.hours.length === 0;
                                  return (
                                    <div
                                      key={entry.date}
                                      className={`rounded px-1.5 py-1.5 text-center text-[10px] leading-tight min-w-[70px] ${
                                        isClosed
                                          ? isDarkMode
                                            ? "bg-red-950/30 text-red-400"
                                            : "bg-red-50 text-red-500"
                                          : isDarkMode
                                            ? "bg-gray-700/50 text-gray-300"
                                            : "bg-gray-100 text-gray-700"
                                      }`}
                                    >
                                      <div className="font-semibold mb-0.5">
                                        {entry.date}
                                      </div>
                                      {isClosed ? (
                                        <div>Closed</div>
                                      ) : (
                                        entry.hours.map((h, i) => (
                                          <div key={i}>{h}</div>
                                        ))
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                      </div>

                      {/* Referral info (callHandler = public-facing) */}
                      {pharmacy.referralCallHandler && (
                        <div
                          className={`flex items-start gap-2 text-xs mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                          <span>{pharmacy.referralCallHandler}</span>
                        </div>
                      )}

                      {/* Clinician-only referral info */}
                      {pharmacy.referralOther && (
                        <div
                          className={`flex items-start gap-2 text-xs mt-1 px-2 py-1.5 rounded ${
                            isDarkMode
                              ? "bg-blue-950/20 text-blue-300 border border-blue-900/30"
                              : "bg-blue-50 text-blue-800 border border-blue-100"
                          }`}
                        >
                          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                          <div>
                            <div className="font-medium text-[10px] mb-0.5">
                              Professional Referral Information
                            </div>
                            <span className="text-[11px]">
                              {pharmacy.referralOther}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Professional referral information */}
                      {pharmacy.professionalReferralInformation && (
                        <div
                          className={`text-[10px] mt-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                        >
                          {pharmacy.professionalReferralInformation}
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        {/* Action Buttons */}
        <div className="flex gap-3 pt-4 border-t">
          {!showServiceTypeFilter && (
            <Button
              type="button"
              onClick={handleSelectPharmacy}
              disabled={!selectedPharmacy}
              className={`flex-1 ${
                isDarkMode
                  ? "bg-white text-black hover:bg-gray-200"
                  : "bg-slate-900 hover:bg-slate-800"
              }`}
            >
              Select Pharmacy
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
